import { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { API } from "@/App";
import { toast } from "sonner";
import { Pencil, Check, X, History } from "lucide-react";
import "./cabina.css";

const TRATAMIENTOS = ["gravado", "parcial_gravado", "no_objeto", "exento", "impuesto_tercero"];

const chipClass = (t) => (
  t === "gravado" ? "c-val"
    : t === "parcial_gravado" ? "c-capt"
      : t === "no_objeto" ? "c-pend"
        : t === "impuesto_tercero" ? "c-warn"
          : "c-canal"
);

function readCondicion(a = {}) {
  const parts = [];
  if (a.es_impuesto_tercero !== undefined) parts.push("categoría marcada como impuesto de tercero");
  if (a.categorias) parts.push("categoría en " + a.categorias.join(" / "));
  if (a.grupo) parts.push("grupo " + a.grupo);
  if (a.ambito) parts.push("ámbito " + a.ambito);
  return parts.join(" · ") || "cualquier caso";
}

export default function CabinaMatrizFiscal() {
  const [cat, setCat] = useState(null);
  const [reglas, setReglas] = useState([]);
  const [editId, setEditId] = useState(null);
  const [draft, setDraft] = useState({});
  const [saving, setSaving] = useState(false);
  const [historial, setHistorial] = useState({ open: false, rows: [] });
  const [canEdit, setCanEdit] = useState(false);

  const load = useCallback(async () => {
    const r = await axios.get(`${API}/cabina/catalogo`);
    setCat(r.data);
    setReglas((r.data?.matriz_fiscal || []).slice().sort((a, b) => (a.prioridad || 0) - (b.prioridad || 0)));
  }, []);

  useEffect(() => {
    load();
    // Detectar permiso vía /api/auth/me
    axios.get(`${API}/auth/me`).then((r) => {
      const u = r.data || {};
      setCanEdit(u.role === "admin" || (u.modules || {}).cabina_matriz_fiscal === true);
    }).catch(() => setCanEdit(false));
  }, [load]);

  const startEdit = (r) => {
    setEditId(r.id);
    setDraft({
      prioridad: r.prioridad ?? 100,
      codigo: r.codigo || "",
      nombre: r.nombre || "",
      // Guardamos en fracción (0.16) pero mostramos en %
      tasa_efectiva_costo: r.tasa_efectiva_costo ?? 0,
      porcentaje_base_gravada: r.porcentaje_base_gravada ?? null,
      tratamiento: r.tratamiento || "gravado",
      nota: r.nota || "",
      activo: r.activo !== false,
    });
  };
  const cancelEdit = () => { setEditId(null); setDraft({}); };

  const saveEdit = async (r) => {
    setSaving(true);
    try {
      const body = { ...draft };
      // Sanitizar números
      body.tasa_efectiva_costo = Number(body.tasa_efectiva_costo) || 0;
      body.prioridad = parseInt(body.prioridad) || 0;
      if (body.porcentaje_base_gravada === "" || body.porcentaje_base_gravada == null) {
        delete body.porcentaje_base_gravada;
      } else {
        body.porcentaje_base_gravada = Number(body.porcentaje_base_gravada);
      }
      const resp = await axios.patch(`${API}/cabina/matriz-fiscal/${r.id}`, body);
      if (resp.data?.changed) {
        toast.success(`Regla ${r.codigo} actualizada · v${resp.data.version}`);
      } else {
        toast.info("Sin cambios");
      }
      await load();
      setEditId(null);
      setDraft({});
    } catch (e) {
      toast.error(e.response?.data?.detail || "Error al guardar");
    } finally {
      setSaving(false);
    }
  };

  const showHistorial = async (r) => {
    try {
      const resp = await axios.get(`${API}/cabina/matriz-fiscal/${r.id}/historial`);
      setHistorial({ open: true, rows: resp.data.historial || [], codigo: r.codigo });
    } catch (e) {
      toast.error("No se pudo cargar el historial");
    }
  };

  return (
    <div className="cv" data-testid="cabina-matriz-fiscal">
      <div className="head">
        <div>
          <h2>Matriz fiscal</h2>
          <p>Editable. Cambia una tasa y mira cómo se recalcula todo lo que aún no está validado. {canEdit ? null : <b style={{ color: "var(--cv-night)" }}>· Sin permiso de edición</b>}</p>
        </div>
      </div>

      <div className="card">
        <div className="card-h"><h3>Reglas vigentes</h3><span className="sub">{reglas.length} reglas · se evalúan por prioridad ascendente</span></div>
        <div className="tw">
          <table>
            <thead><tr>
              <th className="r">Prio</th>
              <th>Código</th>
              <th>Regla</th>
              <th>Aplica cuando</th>
              <th className="r">Tasa efectiva</th>
              <th className="r">% Base gravada</th>
              <th>Tratamiento</th>
              <th>Nota</th>
              <th className="r">Ver.</th>
              {canEdit && <th className="r">Acciones</th>}
            </tr></thead>
            <tbody>
              {reglas.map((r) => {
                const editing = editId === r.id;
                return (
                  <tr key={r.id} data-testid={`matriz-regla-${r.codigo}`}>
                    <td className="r mono">
                      {editing ? (
                        <input type="number" value={draft.prioridad} onChange={(e) => setDraft({ ...draft, prioridad: e.target.value })} style={{ width: 62, textAlign: "right" }} data-testid={`matriz-input-prioridad-${r.codigo}`} />
                      ) : r.prioridad}
                    </td>
                    <td>
                      {editing ? (
                        <input value={draft.codigo} onChange={(e) => setDraft({ ...draft, codigo: e.target.value.toUpperCase() })} style={{ width: 110 }} />
                      ) : (
                        <b className="mono" style={{ color: "var(--cv-blue)" }}>{r.codigo}</b>
                      )}
                    </td>
                    <td>
                      {editing ? (
                        <input value={draft.nombre} onChange={(e) => setDraft({ ...draft, nombre: e.target.value })} style={{ width: 220 }} />
                      ) : r.nombre}
                    </td>
                    <td style={{ fontSize: 11.5, color: "var(--cv-muted)", maxWidth: 320 }}>{readCondicion(r.aplica_si)}</td>
                    <td className="r mono">
                      {editing ? (
                        <input type="number" step="0.01" min="0" max="0.5" value={draft.tasa_efectiva_costo}
                          onChange={(e) => setDraft({ ...draft, tasa_efectiva_costo: e.target.value })}
                          style={{ width: 90, textAlign: "right" }}
                          data-testid={`matriz-input-tasa-${r.codigo}`} />
                      ) : `${((r.tasa_efectiva_costo || 0) * 100).toFixed(2)}%`}
                    </td>
                    <td className="r mono" style={{ color: "var(--cv-muted)" }}>
                      {editing ? (
                        <input type="number" step="0.01" min="0" max="1" value={draft.porcentaje_base_gravada ?? ""}
                          onChange={(e) => setDraft({ ...draft, porcentaje_base_gravada: e.target.value })}
                          placeholder="—" style={{ width: 82, textAlign: "right" }} />
                      ) : ("porcentaje_base_gravada" in r && r.porcentaje_base_gravada != null
                        ? `${(r.porcentaje_base_gravada * 100).toFixed(0)}%`
                        : (r.tasa_efectiva_costo > 0 ? "100%" : "—"))}
                    </td>
                    <td>
                      {editing ? (
                        <select value={draft.tratamiento} onChange={(e) => setDraft({ ...draft, tratamiento: e.target.value })} style={{ width: 130 }}>
                          {TRATAMIENTOS.map((t) => <option key={t} value={t}>{t}</option>)}
                        </select>
                      ) : (
                        <span className={`chip ${chipClass(r.tratamiento)}`}>{r.tratamiento}</span>
                      )}
                    </td>
                    <td style={{ fontSize: 11.5, color: "var(--cv-muted)", maxWidth: 320 }}>
                      {editing ? (
                        <textarea value={draft.nota} onChange={(e) => setDraft({ ...draft, nota: e.target.value })} rows={2} style={{ minWidth: 220 }} />
                      ) : (r.nota || "")}
                    </td>
                    <td className="r mono" style={{ color: "var(--cv-ghost)" }}>v{r.version || 1}</td>
                    {canEdit && (
                      <td className="r">
                        {editing ? (
                          <div className="ibs">
                            <button className="ib pri" onClick={() => saveEdit(r)} disabled={saving} title="Guardar" data-testid={`matriz-save-${r.codigo}`}><Check /></button>
                            <button className="ib" onClick={cancelEdit} title="Cancelar"><X /></button>
                          </div>
                        ) : (
                          <div className="ibs">
                            <button className="ib" onClick={() => startEdit(r)} title="Editar" data-testid={`matriz-edit-${r.codigo}`}><Pencil /></button>
                            {(r.version || 1) > 1 && (
                              <button className="ib" onClick={() => showHistorial(r)} title="Historial" data-testid={`matriz-history-${r.codigo}`}><History /></button>
                            )}
                          </div>
                        )}
                      </td>
                    )}
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>

      <div className="rulebox" style={{ marginTop: 16 }}>
        <b>Cómo se lee.</b> Las reglas se evalúan de menor a mayor prioridad y gana la primera que empata. La tasa efectiva es la que trae embebido el costo del proveedor: el IVA acreditable sale de <span className="mono">costo × t ÷ (1+t)</span>. La utilidad de agencia siempre lleva 16% y eso no depende de la regla. Al editar se crea una versión nueva (v+1) y los servicios ya validados conservan la versión con la que se validaron.
      </div>

      <div className="card" style={{ marginTop: 16 }}>
        <div className="card-h"><h3>Plantillas de costo</h3><span className="sub">componentes que puede tener cada categoría</span></div>
        <div className="tw">
          <table>
            <thead><tr>
              <th>Código</th>
              <th>Nombre</th>
              <th>Aplica a</th>
              <th>Componentes</th>
            </tr></thead>
            <tbody>
              {(cat?.plantillas_costo || []).map((p) => (
                <tr key={p.codigo}>
                  <td><b className="mono" style={{ color: "var(--cv-blue)" }}>{p.codigo}</b></td>
                  <td>{p.nombre}</td>
                  <td style={{ fontSize: 11.5 }}>{(p.aplica_categorias || []).join(", ") || "todas"}</td>
                  <td><div style={{ display: "flex", flexWrap: "wrap", gap: 4 }}>{(p.componentes || []).map((c) => <span key={c.codigo} className="chip c-canal">{c.codigo}</span>)}</div></td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* Modal historial */}
      {historial.open && (
        <div className="mask on" style={{ position: "fixed", inset: 0, background: "rgba(15,23,42,.5)", zIndex: 60, display: "grid", placeItems: "center" }} onClick={() => setHistorial({ open: false, rows: [] })}>
          <div className="modal" style={{ background: "#fff", borderRadius: 12, maxWidth: 720, width: "92%", maxHeight: "80vh", overflow: "auto" }} onClick={(e) => e.stopPropagation()}>
            <div className="modal-h" style={{ padding: "14px 18px", borderBottom: "1px solid #e5e7eb", display: "flex", alignItems: "center", justifyContent: "space-between" }}>
              <h3 style={{ margin: 0 }}>Historial · {historial.codigo}</h3>
              <button className="btn ghost sm" onClick={() => setHistorial({ open: false, rows: [] })}>Cerrar</button>
            </div>
            <div className="modal-b" style={{ padding: 18 }}>
              <div className="tw">
                <table>
                  <thead><tr><th>Ver.</th><th>Vigente desde</th><th>Vigente hasta</th><th className="r">Tasa</th><th>Nota</th></tr></thead>
                  <tbody>
                    {historial.rows.map((h) => (
                      <tr key={h.id}>
                        <td className="mono">v{h.version || 1}</td>
                        <td className="mono" style={{ fontSize: 11.5, color: "var(--cv-muted)" }}>{(h.vigente_desde || "").slice(0, 16).replace("T", " ")}</td>
                        <td className="mono" style={{ fontSize: 11.5, color: "var(--cv-muted)" }}>{h.vigente_hasta ? h.vigente_hasta.slice(0, 16).replace("T", " ") : "vigente"}</td>
                        <td className="r mono">{((h.tasa_efectiva_costo || 0) * 100).toFixed(2)}%</td>
                        <td style={{ fontSize: 11.5, color: "var(--cv-muted)" }}>{h.nota || ""}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
